import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './AdminPage.module.css';

function formatPrice(p) {
  if (p >= 1000000) return (p/1000000).toFixed(1)+' mln zł';
  return Number(p).toLocaleString('pl-PL')+' zł';
}
function timeAgo(d) {
  const days = Math.floor((Date.now()-new Date(d))/86400000);
  if (days === 0) return 'Dziś'; if (days === 1) return 'Wczoraj';
  if (days < 30) return `${days} dni temu`;
  return new Date(d).toLocaleDateString('pl-PL');
}

const API = (path) => fetch(path);
const AAPI = (path, token, opts={}) => fetch(path, { ...opts, headers: { ...opts.headers, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });

export default function AdminPage() {
  const { user, token, isLoggedIn, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [search, setSearch] = useState('');
  const [listingType, setListingType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn || user?.role !== 'admin') navigate('/');
  }, [isLoggedIn, authLoading, user, navigate]);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    const res = await AAPI('/api/admin/listings/pending', token);
    if (res.ok) { const d = await res.json(); setPending(d); }
    setLoading(false);
  }, [token]);

  const fetchStats = useCallback(async () => {
    const res = await AAPI('/api/admin/stats', token);
    if (res.ok) setStats(await res.json());
  }, [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const res = await AAPI(`/api/admin/users?search=${encodeURIComponent(search)}`, token);
    if (res.ok) { const d = await res.json(); setUsers(d.data); }
    setLoading(false);
  }, [token, search]);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    const res = await AAPI(`/api/admin/listings?search=${encodeURIComponent(search)}&type=${listingType}`, token);
    if (res.ok) { const d = await res.json(); setListings(d.data); }
    setLoading(false);
  }, [token, search, listingType]);

  useEffect(() => { if (token) fetchStats(); }, [fetchStats]);
  useEffect(() => { if (tab === 'users' && token) fetchUsers(); }, [tab, fetchUsers]);
  useEffect(() => { if (tab === 'pending' && token) fetchPending(); }, [tab, fetchPending]);
  useEffect(() => { if (tab === 'listings' && token) fetchListings(); }, [tab, fetchListings]);

  const notify = (msg, isErr=false) => { isErr ? setError(msg) : setInfo(msg); setTimeout(() => { setError(null); setInfo(null); }, 4000); };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Usunąć użytkownika ${name}? Jego ogłoszenia pozostaną.`)) return;
    const res = await AAPI(`/api/admin/users/${id}`, token, { method: 'DELETE' });
    res.ok ? (notify('Użytkownik usunięty'), fetchUsers(), fetchStats()) : notify('Błąd usuwania', true);
  };

  const verifyUser = async (id) => {
    const res = await AAPI(`/api/admin/users/${id}/verify`, token, { method: 'PATCH' });
    res.ok ? (notify('Konto zweryfikowane'), fetchUsers()) : notify('Błąd', true);
  };

  const makeAdmin = async (id, current) => {
    const newRole = current === 'admin' ? 'agent' : 'admin';
    if (!window.confirm(`Zmienić rolę na ${newRole}?`)) return;
    const res = await AAPI(`/api/admin/users/${id}/role`, token, { method: 'PATCH', body: JSON.stringify({ role: newRole }) });
    res.ok ? (notify(`Rola zmieniona na ${newRole}`), fetchUsers()) : notify('Błąd', true);
  };

  const approveListing = async (id) => {
    const res = await AAPI(`/api/admin/listings/${id}/approve`, token, { method: 'PATCH' });
    res.ok ? (notify('✓ Ogłoszenie zatwierdzone i opublikowane'), fetchPending(), fetchStats()) : notify('Błąd', true);
  };

  const rejectListing = async (id) => {
    if (!rejectReason.trim()) return notify('Wpisz powód odrzucenia', true);
    const res = await AAPI(`/api/admin/listings/${id}/reject`, token, { method: 'PATCH', body: JSON.stringify({ reason: rejectReason }) });
    res.ok ? (notify('Ogłoszenie odrzucone'), setRejectingId(null), setRejectReason(''), fetchPending(), fetchStats()) : notify('Błąd', true);
  };

  const deleteListing = async (id, title) => {
    if (!window.confirm(`Usunąć ogłoszenie "${title}"?`)) return;
    const res = await AAPI(`/api/admin/listings/${id}`, token, { method: 'DELETE' });
    res.ok ? (notify('Ogłoszenie usunięte'), fetchListings(), fetchStats()) : notify('Błąd usuwania', true);
  };

  if (authLoading || !user) return null;
  if (user.role !== 'admin') return null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Panel admina</h1>
          <p>Zalogowany jako <strong>{user.name}</strong></p>
        </div>
        <Link to="/" className={styles.homeLink}>← Strona główna</Link>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {info && <div className={styles.success}>{info}</div>}

      {/* TABS */}
      <div className={styles.tabs}>
        {['pending','stats','users','listings'].map(t => (
          <button key={t} className={`${styles.tab} ${tab===t ? styles.tabActive : ''}`} onClick={() => setTab(t)}>
            {{ pending: `⏳ Do akceptacji${pending.length ? ' ('+pending.length+')' : ''}`, stats: '📊 Statystyki', users: '👥 Użytkownicy', listings: '🏠 Wszystkie ogłoszenia' }[t]}
          </button>
        ))}
      </div>


      {/* PENDING */}
      {tab === 'pending' && (
        <div>
          {loading ? <div className={styles.loading}>Ładowanie…</div> : pending.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>✓</div>
              <p>Brak ogłoszeń do moderacji</p>
            </div>
          ) : (
            <div className={styles.pendingGrid}>
              {pending.map(l => (
                <div key={l.id} className={styles.pendingCard}>
                  <div className={styles.pendingImg}>
                    {l.mainImageUrl
                      ? <img src={l.mainImageUrl} alt={l.title} className={styles.pendingPhoto}/>
                      : <div className={styles.pendingNoPhoto}>🏠</div>
                    }
                    <span className={`${styles.typeBadge} ${styles['badge_'+l.type]}`}>
                      {{ sprzedaz:'Sprzedaż', wynajem:'Wynajem', nowe:'Nowe' }[l.type]}
                    </span>
                  </div>
                  <div className={styles.pendingBody}>
                    <div className={styles.pendingTitle}>{l.title}</div>
                    <div className={styles.pendingMeta}>
                      <span className={styles.pendingPrice}>
                        {Number(l.price) >= 1000000
                          ? (l.price/1000000).toFixed(1)+' mln zł'
                          : Number(l.price).toLocaleString('pl-PL')+' zł'}
                        {l.type === 'wynajem' ? ' /mies.' : ''}
                      </span>
                      <span>{l.area} m² · {l.rooms} pok. · {l.floor}</span>
                      <span>📍 {l.location}</span>
                    </div>
                    {l.description && <p className={styles.pendingDesc}>{l.description.slice(0, 180)}{l.description.length > 180 ? '…' : ''}</p>}
                    <div className={styles.pendingAgent}>
                      Dodał/a: <strong>{l.user_name}</strong> ({l.user_email})
                      {l.user_phone && <span> · {l.user_phone}</span>}
                    </div>
                    <div className={styles.pendingDate}>Dodano: {new Date(l.created_at).toLocaleString('pl-PL')}</div>

                    {rejectingId === l.id ? (
                      <div className={styles.rejectBox}>
                        <textarea
                          className={styles.rejectInput}
                          placeholder="Podaj powód odrzucenia (zostanie wysłany do użytkownika)…"
                          rows={3}
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                        />
                        <div className={styles.rejectActions}>
                          <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => rejectListing(l.id)}>Wyślij odrzucenie</button>
                          <button className={styles.btnSmall} onClick={() => { setRejectingId(null); setRejectReason(''); }}>Anuluj</button>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.pendingActions}>
                        <button className={styles.btnApprove} onClick={() => approveListing(l.id)}>✓ Zatwierdź i opublikuj</button>
                        <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => { setRejectingId(l.id); setRejectReason(''); }}>✗ Odrzuć</button>
                        <a href={`/ogloszenia/${l.id}`} target="_blank" rel="noreferrer" className={styles.btnSmall}>Podgląd</a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STATS */}
      {tab === 'stats' && stats && (
        <div>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}><span className={styles.statNum}>{stats.listings}</span><span className={styles.statLbl}>Ogłoszeń</span></div>
            <div className={styles.statCard}><span className={styles.statNum}>{stats.users}</span><span className={styles.statLbl}>Użytkowników</span></div>
            <div className={styles.statCard}><span className={styles.statNum}>{stats.newListingsToday}</span><span className={styles.statLbl}>Nowych dziś</span></div>
            <div className={styles.statCard}><span className={styles.statNum}>{stats.images}</span><span className={styles.statLbl}>Zdjęć</span></div>
            <div className={`${styles.statCard} ${stats.unverifiedUsers > 0 ? styles.statWarn : ''}`}>
              <span className={styles.statNum}>{stats.unverifiedUsers}</span>
              <span className={styles.statLbl}>Niezweryfikowanych</span>
            </div>
          </div>
          <div className={styles.typeGrid}>
            {Object.entries(stats.byType || {}).map(([type, count]) => (
              <div key={type} className={styles.typeCard}>
                <span className={`${styles.typeBadge} ${styles['badge_'+type]}`}>{{ sprzedaz:'Sprzedaż', wynajem:'Wynajem', nowe:'Nowe' }[type]}</span>
                <span className={styles.typeNum}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* USERS */}
      {tab === 'users' && (
        <div>
          <div className={styles.toolbar}>
            <input className={styles.search} type="text" placeholder="Szukaj po nazwie lub e-mail…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && fetchUsers()} />
            <button className={styles.searchBtn} onClick={fetchUsers}>Szukaj</button>
          </div>
          {loading ? <div className={styles.loading}>Ładowanie…</div> : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr>
                  <th>Użytkownik</th><th>E-mail</th><th>Rola</th>
                  <th>Ogłoszenia</th><th>Status</th><th>Dołączył/a</th><th>Akcje</th>
                </tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className={u.id === user.id ? styles.self : ''}>
                      <td><strong>{u.name}</strong></td>
                      <td className={styles.muted}>{u.email}</td>
                      <td>
                        <span className={`${styles.roleBadge} ${u.role==='admin' ? styles.roleAdmin : styles.roleAgent}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className={styles.center}>{u.listing_count}</td>
                      <td>
                        {u.email_verified
                          ? <span className={styles.verified}>✓ OK</span>
                          : <span className={styles.unverified}>✗ Nie</span>}
                      </td>
                      <td className={styles.muted}>{timeAgo(u.created_at)}</td>
                      <td>
                        <div className={styles.actions}>
                          {!u.email_verified && (
                            <button className={styles.btnSmall} onClick={() => verifyUser(u.id)}>Weryfikuj</button>
                          )}
                          {u.id !== user.id && (
                            <button className={`${styles.btnSmall} ${u.role==='admin' ? styles.btnWarn : ''}`} onClick={() => makeAdmin(u.id, u.role)}>
                              {u.role==='admin' ? 'Odbierz admina' : 'Daj admina'}
                            </button>
                          )}
                          {u.id !== user.id && (
                            <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => deleteUser(u.id, u.name)}>Usuń</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan="7" className={styles.empty}>Brak wyników</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* LISTINGS */}
      {tab === 'listings' && (
        <div>
          <div className={styles.toolbar}>
            <input className={styles.search} type="text" placeholder="Szukaj po tytule lub lokalizacji…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && fetchListings()} />
            <select className={styles.typeSelect} value={listingType} onChange={e => { setListingType(e.target.value); }}>
              <option value="all">Wszystkie typy</option>
              <option value="sprzedaz">Sprzedaż</option>
              <option value="wynajem">Wynajem</option>
              <option value="nowe">Nowe</option>
            </select>
            <button className={styles.searchBtn} onClick={fetchListings}>Szukaj</button>
          </div>
          {loading ? <div className={styles.loading}>Ładowanie…</div> : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr>
                  <th>Tytuł</th><th>Typ</th><th>Cena</th><th>Lokalizacja</th>
                  <th>Agent</th><th>Zdjęcia</th><th>Dodano</th><th>Akcje</th>
                </tr></thead>
                <tbody>
                  {listings.map(l => (
                    <tr key={l.id}>
                      <td>
                        <Link to={`/ogloszenia/${l.id}`} className={styles.listingLink} target="_blank">{l.title}</Link>
                      </td>
                      <td><span className={`${styles.typeBadge} ${styles['badge_'+l.type]}`}>{{ sprzedaz:'Sprzedaż', wynajem:'Wynajem', nowe:'Nowe' }[l.type]}</span></td>
                      <td className={styles.price}>{formatPrice(l.price)}</td>
                      <td className={styles.muted}>{l.location}</td>
                      <td>
                        <div className={styles.agentCell}>
                          <span>{l.user_name || '—'}</span>
                          <span className={styles.muted}>{l.user_email}</span>
                        </div>
                      </td>
                      <td className={styles.center}>{l.image_count}</td>
                      <td className={styles.muted}>{timeAgo(l.created_at)}</td>
                      <td>
                        <button className={`${styles.btnSmall} ${styles.btnDanger}`} onClick={() => deleteListing(l.id, l.title)}>Usuń</button>
                      </td>
                    </tr>
                  ))}
                  {listings.length === 0 && <tr><td colSpan="8" className={styles.empty}>Brak wyników</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
